/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useMemo } from 'react';
import { Spin, message, Select, Button, DatePicker, Tag } from 'antd';
import { DownloadOutlined, ReloadOutlined } from '@ant-design/icons';
import {
    LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
    XAxis, YAxis, CartesianGrid, Tooltip, Legend,
    ResponsiveContainer, LabelList,
} from 'recharts';
import FleetService from '../../../services/FleetService';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import moment from 'moment';

const NAVY   = '#1a3a5c';
const COLORS = ['#1a3a5c','#2e86c1','#52c41a','#faad14','#f5222d','#722ed1','#13c2c2','#fa8c16'];
const toN    = v => Number(v || 0);

function KPI({ label, value, color=NAVY, sub, warn }) {
    return (
        <div style={{ background:'#fff', border:`1px solid ${warn?'#ffa39e':'#e8e8e8'}`,
            borderRadius:8, padding:'12px 16px', borderTop:`3px solid ${color}`, flex:1, minWidth:120 }}>
            <div style={{ fontSize:11, color:'#888', marginBottom:3 }}>{label}</div>
            <div style={{ fontSize:20, fontWeight:700, color }}>{value}</div>
            {sub && <div style={{ fontSize:11, color:'#aaa', marginTop:2 }}>{sub}</div>}
        </div>
    );
}
function ChartCard({ title, children, height=270 }) {
    return (
        <div style={{ background:'#fff', border:'1px solid #e8e8e8', borderRadius:8,
            padding:'14px 16px', marginBottom:14 }}>
            <div style={{ fontWeight:600, fontSize:13, color:NAVY, marginBottom:10 }}>{title}</div>
            <div style={{ height }}>{children}</div>
        </div>
    );
}

const statusColor = s =>
    s==='Active'   ? '#52c41a' :
    s==='Overdue'  ? '#f5222d' :
    s==='Returned' ? '#2e86c1' : '#aaa';

export default function FleetAnalyticsDeployments() {
    const [deps,     setDeps]     = useState([]);
    const [loading,  setLoading]  = useState(true);
    const [region,   setRegion]   = useState('');
    const [programme,setProgramme]= useState('');
    const [facility, setFacility] = useState('');
    const [depType,  setDepType]  = useState('');
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo,   setDateTo]   = useState('');

    const load = async () => {
        setLoading(true);
        try {
            const r = await FleetService.getDeployments();
            if (Number(r?.data?.code) === 200) {
                const raw = r.data.result?.deployments || r.data.result || [];
                setDeps(raw);
            }
        } catch (e) { message.error('Failed to load deployment data'); }
        setLoading(false);
    };

    useEffect(() => { load(); }, []);

    // ── Flatten: one row per vehicle-in-deployment ────────────────────────────
    const rows = useMemo(() => {
        const out = [];
        deps.forEach(dep => {
            const vehicles = dep.vehicles || [];
            if (vehicles.length === 0) {
                out.push({ ...dep, reg:'—', vehicleType:'—', vehicleRegion:'', vehicleProg:'', vehicleFacility:'', vehicleStatus: dep.status });
            } else {
                vehicles.forEach(v => {
                    out.push({
                        ...dep,
                        reg:            v.registration_no || '—',
                        vehicleType:    v.vehicle_type,
                        vehicleRegion:  v.region,
                        vehicleProg:    v.assigned_programme,
                        vehicleFacility:v.facility,
                        vehicleStatus:  v.vehicle_status || dep.status,
                        driverName:     v.driver_name,
                    });
                });
            }
        });
        return out;
    }, [deps]);

    // ── Filters ───────────────────────────────────────────────────────────────
    const regions    = useMemo(() => [...new Set(rows.map(r=>r.vehicleRegion).filter(Boolean))], [rows]);
    const programmes = useMemo(() => [...new Set(rows.map(r=>r.vehicleProg).filter(Boolean))], [rows]);
    const facilities = useMemo(() => [...new Set(rows.map(r=>r.vehicleFacility).filter(Boolean))], [rows]);
    const depTypes   = useMemo(() => [...new Set(rows.map(r=>r.deployment_type).filter(Boolean))], [rows]);

    const filtered = useMemo(() => rows.filter(r => {
        if (region    && r.vehicleRegion  !== region)    return false;
        if (programme && r.vehicleProg    !== programme) return false;
        if (facility  && r.vehicleFacility!== facility)  return false;
        if (depType   && r.deployment_type!== depType)   return false;
        if (dateFrom  && r.date_out < dateFrom)          return false;
        if (dateTo    && r.date_out > dateTo)            return false;
        return true;
    }), [rows, region, programme, facility, depType, dateFrom, dateTo]);

    // ── KPIs ──────────────────────────────────────────────────────────────────
    const total       = new Set(filtered.map(r=>r.id)).size;
    const active      = filtered.filter(r=>r.vehicleStatus==='Out'||r.status==='Active').length;
    const overdue     = filtered.filter(r=>r.vehicleStatus==='Overdue'||r.status==='Overdue').length;
    const intl        = filtered.filter(r=>r.is_international==1).length;
    const returned    = filtered.filter(r=>r.vehicleStatus==='Returned'||r.status==='Returned').length;
    const avgDaysOut  = filtered.length
        ? Math.round(filtered.reduce((s,r)=>s+toN(r.days_out),0)/filtered.length) : 0;

    // ── Monthly deployments out (line chart) ──────────────────────────────────
    const monthlyOut = useMemo(() => {
        const m = {};
        filtered.forEach(r => {
            if (!r.date_out) return;
            const mo = moment(r.date_out).format('MMM YY');
            if (!m[mo]) m[mo] = { month:mo, total:0, international:0, national:0, overdue:0 };
            m[mo].total++;
            if (r.is_international==1) m[mo].international++;
            else m[mo].national++;
            if (r.status==='Overdue' || r.vehicleStatus==='Overdue') m[mo].overdue++;
        });
        return Object.values(m).sort((a,b)=>moment(a.month,'MMM YY')-moment(b.month,'MMM YY')).slice(-18);
    }, [filtered]);

    // ── By region comparison (line chart — vehicles deployed per region over time) ──
    const byRegionMonth = useMemo(() => {
        const months = {};
        const regionSet = new Set();
        filtered.filter(r=>r.vehicleRegion && r.date_out).forEach(r => {
            const mo = moment(r.date_out).format('MMM YY');
            regionSet.add(r.vehicleRegion);
            if (!months[mo]) months[mo] = { month:mo };
            months[mo][r.vehicleRegion] = (months[mo][r.vehicleRegion] || 0) + 1;
        });
        return {
            data:    Object.values(months).sort((a,b)=>moment(a.month,'MMM YY')-moment(b.month,'MMM YY')).slice(-12),
            regions: [...regionSet],
        };
    }, [filtered]);

    // ── By programme ──────────────────────────────────────────────────────────
    const byProgramme = useMemo(() => {
        const m = {};
        filtered.filter(r=>r.vehicleProg).forEach(r => {
            if (!m[r.vehicleProg]) m[r.vehicleProg] = { name:r.vehicleProg, total:0, active:0, overdue:0, intl:0 };
            m[r.vehicleProg].total++;
            if (r.status==='Active') m[r.vehicleProg].active++;
            if (r.status==='Overdue'||r.vehicleStatus==='Overdue') m[r.vehicleProg].overdue++;
            if (r.is_international==1) m[r.vehicleProg].intl++;
        });
        return Object.values(m).sort((a,b)=>b.total-a.total);
    }, [filtered]);

    // ── By type pie ───────────────────────────────────────────────────────────
    const byType = useMemo(() => {
        const m = {};
        filtered.forEach(r => {
            const k = r.deployment_type || 'Other';
            m[k] = (m[k] || 0) + 1;
        });
        return Object.entries(m).map(([name,value])=>({name,value}));
    }, [filtered]);

    // ── Duration distribution ─────────────────────────────────────────────────
    const durationDist = useMemo(() => {
        const m = { '1–3 days':0, '4–7 days':0, '1–2 weeks':0, '2–4 weeks':0, '>4 weeks':0 };
        filtered.forEach(r => {
            const d = toN(r.days_out);
            if (d<=3) m['1–3 days']++;
            else if (d<=7) m['4–7 days']++;
            else if (d<=14) m['1–2 weeks']++;
            else if (d<=28) m['2–4 weeks']++;
            else m['>4 weeks']++;
        });
        return Object.entries(m).map(([name,count])=>({name,count}));
    }, [filtered]);

    const exportXlsx = () => {
        const ws = XLSX.utils.json_to_sheet(filtered.map(r=>({
            'Event':          r.event_name,
            'Type':           r.deployment_type,
            'Date Out':       r.date_out,
            'Expected Return':r.expected_return,
            'Status':         r.status,
            'Days Out':       r.days_out,
            'International':  r.is_international==1?'Yes':'No',
            'Reg No':         r.reg,
            'Vehicle Type':   r.vehicleType,
            'Region':         r.vehicleRegion,
            'Programme':      r.vehicleProg,
            'Facility':       r.vehicleFacility,
            'Driver':         r.driverName,
            'Vehicle Status': r.vehicleStatus,
        })));
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Deployments');
        saveAs(new Blob([XLSX.write(wb,{bookType:'xlsx',type:'array'})]),
            `Fleet_Deployments_${new Date().toISOString().slice(0,10)}.xlsx`);
    };

    const filterBar = (
        <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginBottom:16, alignItems:'center' }}>
            <Select allowClear placeholder="Region" size="small" style={{ width:120 }}
                value={region||undefined} onChange={v=>setRegion(v||'')}>
                {regions.map(r=><Select.Option key={r} value={r}>{r}</Select.Option>)}
            </Select>
            <Select allowClear placeholder="Programme" size="small" style={{ width:140 }}
                value={programme||undefined} onChange={v=>setProgramme(v||'')}>
                {programmes.map(p=><Select.Option key={p} value={p}>{p}</Select.Option>)}
            </Select>
            <Select allowClear placeholder="Facility" size="small" style={{ width:150 }}
                value={facility||undefined} onChange={v=>setFacility(v||'')}>
                {facilities.map(f=><Select.Option key={f} value={f}>{f}</Select.Option>)}
            </Select>
            <Select allowClear placeholder="Event type" size="small" style={{ width:140 }}
                value={depType||undefined} onChange={v=>setDepType(v||'')}>
                {depTypes.map(t=><Select.Option key={t} value={t}>{t}</Select.Option>)}
            </Select>
            <DatePicker size="small" placeholder="From" style={{ width:110 }} onChange={(_,s)=>setDateFrom(s||'')}/>
            <DatePicker size="small" placeholder="To"   style={{ width:110 }} onChange={(_,s)=>setDateTo(s||'')}/>
            <Button size="small" icon={<ReloadOutlined/>} onClick={load}>Refresh</Button>
            <Button size="small" type="primary" icon={<DownloadOutlined/>}
                onClick={exportXlsx} disabled={!filtered.length}>Export</Button>
        </div>
    );

    return (
        <div className="page-content" style={{ padding:'20px 24px' }}>
            <div style={{ marginBottom:16 }}>
                <h4 style={{ margin:0, fontWeight:700, color:NAVY }}>Deployment Analytics</h4>
                <div style={{ fontSize:12, color:'#888', marginTop:2 }}>
                    National events · cross-border trips · vehicles out by region/programme · overdue tracking
                </div>
            </div>

            {filterBar}

            <Spin spinning={loading}>
                {/* KPIs */}
                <div style={{ display:'flex', gap:10, marginBottom:16, flexWrap:'wrap' }}>
                    <KPI label="Total deployments"   value={total}         color={NAVY}/>
                    <KPI label="Vehicles currently out" value={active}     color="#52c41a" sub="active deployments"/>
                    <KPI label="Overdue returns"     value={overdue}       color={overdue>0?'#f5222d':'#52c41a'}
                        sub="past expected return" warn={overdue>0}/>
                    <KPI label="International / cross-border" value={intl} color="#722ed1" sub="is_international flag"/>
                    <KPI label="Returned"            value={returned}      color="#2e86c1"/>
                    <KPI label="Avg days out"        value={`${avgDaysOut}d`} color={avgDaysOut>14?'#fa8c16':NAVY}/>
                </div>

                {/* Row 1: Monthly trend + type pie */}
                <div style={{ display:'grid', gridTemplateColumns:'2fr 1fr', gap:14 }}>
                    <ChartCard title="Monthly deployments — total vs international vs overdue" height={260}>
                        <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={monthlyOut} margin={{top:5,right:20,left:5,bottom:5}}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0"/>
                                <XAxis dataKey="month" tick={{fontSize:10}}/>
                                <YAxis tick={{fontSize:10}} allowDecimals={false}/>
                                <Tooltip/>
                                <Legend wrapperStyle={{fontSize:11}}/>
                                <Line type="monotone" dataKey="total"         name="Total"         stroke={NAVY}     strokeWidth={2} dot={{r:3}}/>
                                <Line type="monotone" dataKey="national"      name="National"      stroke="#2e86c1" strokeWidth={2} dot={{r:3}} strokeDasharray="5 3"/>
                                <Line type="monotone" dataKey="international" name="International" stroke="#722ed1" strokeWidth={2} dot={{r:3}}/>
                                <Line type="monotone" dataKey="overdue"       name="Overdue"       stroke="#f5222d" strokeWidth={1.5} dot={{r:3}} strokeDasharray="4 2"/>
                            </LineChart>
                        </ResponsiveContainer>
                    </ChartCard>

                    <ChartCard title="By deployment type" height={260}>
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Pie data={byType} dataKey="value" nameKey="name"
                                    cx="50%" cy="50%" outerRadius={90} labelLine={false}
                                    label={({name,percent})=>`${name} ${(percent*100).toFixed(0)}%`}>
                                    {byType.map((_,i)=><Cell key={i} fill={COLORS[i%COLORS.length]}/>)}
                                </Pie>
                                <Tooltip/>
                                <Legend iconSize={9} wrapperStyle={{fontSize:10}}/>
                            </PieChart>
                        </ResponsiveContainer>
                    </ChartCard>
                </div>

                {/* Row 2: Region comparison lines + duration */}
                <div style={{ display:'grid', gridTemplateColumns:'1.5fr 1fr', gap:14 }}>
                    <ChartCard title="Vehicles deployed by region over time (line comparison)" height={260}>
                        <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={byRegionMonth.data} margin={{top:5,right:20,left:5,bottom:5}}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0"/>
                                <XAxis dataKey="month" tick={{fontSize:10}}/>
                                <YAxis tick={{fontSize:10}} allowDecimals={false}/>
                                <Tooltip/>
                                <Legend wrapperStyle={{fontSize:11}}/>
                                {byRegionMonth.regions.map((r,i)=>(
                                    <Line key={r} type="monotone" dataKey={r} name={r}
                                        stroke={COLORS[i%COLORS.length]} strokeWidth={2} dot={{r:3}}/>
                                ))}
                            </LineChart>
                        </ResponsiveContainer>
                    </ChartCard>

                    <ChartCard title="Deployment duration distribution" height={260}>
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={durationDist} margin={{top:5,right:10,left:5,bottom:10}}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0"/>
                                <XAxis dataKey="name" tick={{fontSize:10}}/>
                                <YAxis tick={{fontSize:10}} allowDecimals={false}/>
                                <Tooltip/>
                                <Bar dataKey="count" name="Deployments" fill={NAVY} radius={[4,4,0,0]}>
                                    {durationDist.map((_,i)=><Cell key={i} fill={COLORS[i%COLORS.length]}/>)}
                                    <LabelList dataKey="count" position="top" style={{fontSize:10}}/>
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </ChartCard>
                </div>

                {/* By programme stacked bar */}
                {byProgramme.length > 0 && (
                    <ChartCard title="Deployments by programme — total / active / overdue / international" height={260}>
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={byProgramme} margin={{top:5,right:10,left:5,bottom:40}}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0"/>
                                <XAxis dataKey="name" tick={{fontSize:10}} angle={-25} textAnchor="end" height={50}/>
                                <YAxis tick={{fontSize:10}} allowDecimals={false}/>
                                <Tooltip/>
                                <Legend wrapperStyle={{fontSize:11}}/>
                                <Bar dataKey="total"   name="Total"         fill={NAVY}     radius={[0,0,0,0]}/>
                                <Bar dataKey="active"  name="Active"        fill="#52c41a"  radius={[0,0,0,0]}/>
                                <Bar dataKey="overdue" name="Overdue"       fill="#f5222d"  radius={[0,0,0,0]}/>
                                <Bar dataKey="intl"    name="International" fill="#722ed1"  radius={[4,4,0,0]}/>
                            </BarChart>
                        </ResponsiveContainer>
                    </ChartCard>
                )}

                {/* Overdue list */}
                {overdue > 0 && (
                    <div style={{background:'#fff1f0',border:'1px solid #ffa39e',borderRadius:8,padding:'12px 16px',marginTop:4}}>
                        <div style={{fontWeight:600,color:'#cf1322',marginBottom:8}}>
                            🔴 {overdue} vehicle{overdue>1?'s':''} overdue for return
                        </div>
                        <div style={{overflowX:'auto'}}>
                            <table style={{width:'100%',fontSize:12,borderCollapse:'collapse'}}>
                                <thead>
                                    <tr style={{background:'#fff1f0',borderBottom:'1px solid #ffa39e'}}>
                                        {['Reg No','Event','Type','Out date','Expected return','Days overdue','International','Region'].map(h=>
                                            <th key={h} style={{padding:'6px 10px',textAlign:'left',fontWeight:600,fontSize:11}}>{h}</th>
                                        )}
                                    </tr>
                                </thead>
                                <tbody>
                                    {filtered.filter(r=>r.status==='Overdue'||r.vehicleStatus==='Overdue').map((r,i)=>(
                                        <tr key={i} style={{borderBottom:'1px solid #ffe8e8'}}>
                                            <td style={{padding:'6px 10px',fontWeight:600}}>{r.reg}</td>
                                            <td style={{padding:'6px 10px'}}>{r.event_name}</td>
                                            <td style={{padding:'6px 10px'}}>{r.deployment_type}</td>
                                            <td style={{padding:'6px 10px'}}>{r.date_out}</td>
                                            <td style={{padding:'6px 10px'}}>{r.expected_return}</td>
                                            <td style={{padding:'6px 10px'}}><Tag color="red">{r.days_overdue}d</Tag></td>
                                            <td style={{padding:'6px 10px'}}>{r.is_international==1?<Tag color="purple">Yes</Tag>:'No'}</td>
                                            <td style={{padding:'6px 10px'}}>{r.vehicleRegion||'—'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {!loading && filtered.length === 0 && (
                    <div style={{textAlign:'center',padding:60,color:'#aaa',border:'2px dashed #e8e8e8',borderRadius:8}}>
                        <div style={{fontSize:32,marginBottom:12}}>🚐</div>
                        <div style={{fontWeight:600,marginBottom:4}}>No deployment data</div>
                        <div style={{fontSize:12}}>Log vehicle deployments to national events or cross-border trips.</div>
                    </div>
                )}
            </Spin>
        </div>
    );
}